import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import { useDispatch, useSelector } from 'react-redux'
import { State } from '../../types/state'
import { Nursery } from '../../types/nursery'
import { PageBanner } from '../Banners/PageBanner'
import AppNavBar from '../Header/AppNavBar'
import ButtonLink from '../Buttons/ButtonLink'

// Dodanie funkcji mapującej typ handlu na etykiety
const getTradeTypeLabel = (tradeType: string): string => {
  switch (tradeType) {
    case 'retail':
      return 'Handel detaliczny'
    case 'wholesale':
      return 'Handel hurtowy'
    case 'both':
      return 'Hurtowy i detaliczny'
    default:
      return tradeType
  }
}

// Interfejs dla grupowanych roślin
interface GroupedPlants {
  [category: string]: Array<{
    plant: {
      id: number
      name: string
      latinName: string
      image: string
      category: string
      imagePresignedUrl?: string
    }
    availability: string
    price?: string
  }>
}

const NurseryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const requests = useRequests()
  const { username, isSuperadmin } = useSelector((state: State) => state.user)

  const [nursery, setNursery] = useState<Nursery | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [mainImage, setMainImage] = useState<string>('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false)
  const [openedImage, setOpenedImage] = useState<string | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({})

  // Funkcja do grupowania roślin według kategorii
  const groupPlantsByCategory = (plants: any[]): GroupedPlants => {
    return plants.reduce((acc, item) => {
      const category = item.plant.category
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(item)
      return acc
    }, {})
  }

  // Funkcja do przełączania stanu rozwijania kategorii
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  // Funkcje do obsługi powiększania zdjęć
  const openImageModal = (imageUrl: string) => {
    setOpenedImage(imageUrl)
  }

  const closeImageModal = () => {
    setOpenedImage(null)
  }

  // Pobieranie danych szkółki
  useEffect(() => {
    const fetchNurseryDetails = async () => {
      setLoading(true)
      try {
        if (!id) {
          setError('Nieprawidłowy identyfikator szkółki')
          setLoading(false)
          return
        }

        const response = await requests.getNursery(parseInt(id))
        const nurseryData = response.data

        // Po załadowaniu szkółki, pobieramy presigned URL dla zdjęć
        try {
          const presignedUrlsResponse = await requests.getNurseryPresignedUrls(nurseryData.id)
          // Dodajemy presigned URL do danych szkółki
          nurseryData.imagePresignedUrl = presignedUrlsResponse.data.imagePresignedUrl || nurseryData.image
          nurseryData.imagesPresignedUrls = presignedUrlsResponse.data.imagesPresignedUrls ||
            (nurseryData.images ? nurseryData.images.map((img: string) => img) : [])

          // Ustaw główne zdjęcie od razu po załadowaniu
          setMainImage(nurseryData.imagePresignedUrl || nurseryData.image || '/placeholder-image.jpg')

          // Pobierz presigned URL dla każdej rośliny
          // if (nurseryData.nurseryPlants && nurseryData.nurseryPlants.length > 0) {
          //   await Promise.all(
          //     nurseryData.nurseryPlants.map(async (item: any) => {
          //       try {
          //         const plantPresignedUrlsResponse = await requests.getPlantPresignedUrls(item.plant.id)
          //         item.plant.imagePresignedUrl = plantPresignedUrlsResponse.data.imagePresignedUrl || item.plant.image
          //       } catch (error) {
          //         console.error(`Błąd podczas pobierania presigned URL dla rośliny ${item.plant.id}:`, error)
          //         item.plant.imagePresignedUrl = item.plant.image
          //       }
          //     })
          //   )
          // }
        } catch (error) {
          console.error('Błąd podczas pobierania presigned URL dla zdjęć szkółki:', error)
          nurseryData.imagePresignedUrl = nurseryData.image
          if (nurseryData.images) {
            nurseryData.imagesPresignedUrls = nurseryData.images.map((img: string) => img)
          }
          setMainImage(nurseryData.image || '/placeholder-image.jpg')
        }

        setNursery(nurseryData)
        setError(null)
      } catch (err) {
        console.error('Błąd pobierania danych szkółki:', err)
        setError('Nie udało się pobrać szczegółów szkółki.')
      } finally {
        setLoading(false)
      }
    }

    fetchNurseryDetails()
  }, [id, requests])

  const handleImageSelect = (image: string) => {
    setMainImage(image)
  }

  const handleDeleteClick = async () => {
    if (!id) return

    try {
      await requests.deleteNursery(parseInt(id))
      navigate('/nurseries')
    } catch (err) {
      console.error('Błąd usuwania szkółki:', err)
      setError('Nie udało się usunąć szkółki.')
    }
  }

  if (loading) {
    return (
      <div className='text-center pt-100 pb-100'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
      </div>
    )
  }

  if (error || (nursery == null)) {
    return (
      <div className='container pt-60 pb-60'>
        <div className='alert alert-danger' role='alert'>
          {error || 'Nie znaleziono szkółki.'}
        </div>
        <div className='text-center mt-30'>
          <Link to='/nurseries' className='btn btn__primary'>
            Powrót do listy szkółek
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        centerText={nursery.name}
        rightIcons={[]}
      />
      <div className='container tw-py-10 md:tw-py-16'>
        <div className='row'>
          <div className='col-lg-5'>
            <div className='nursery-gallery mb-40'>
              <div className='nursery-gallery__main mb-3'>
                <img
                  src={mainImage || '/placeholder-image.jpg'}
                  alt={nursery.name}
                  className='img-fluid'
                  onClick={() => openImageModal(mainImage)}
                  style={{ cursor: 'pointer' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.onerror = null
                    target.src = '/placeholder-image.jpg'
                  }}
                />
              </div>

              {nursery.images && nursery.images.length > 0 && (
                <div className='nursery-gallery__thumbs'>
                  <div
                    className={`nursery-gallery__thumb ${mainImage === (nursery.imagePresignedUrl || nursery.image) ? 'active' : ''}`}
                    onClick={() => handleImageSelect(nursery.imagePresignedUrl || nursery.image || '/placeholder-image.jpg')}
                  >
                    <img
                      src={nursery.imagePresignedUrl || nursery.image || '/placeholder-image.jpg'}
                      alt={nursery.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.onerror = null
                        target.src = '/placeholder-image.jpg'
                      }}
                    />
                  </div>

                  {nursery.images.map((img, index) => (
                    <div
                      key={index}
                      className={`nursery-gallery__thumb ${mainImage === (nursery.imagesPresignedUrls?.[index] || img) ? 'active' : ''}`}
                      onClick={() => handleImageSelect(nursery.imagesPresignedUrls?.[index] || img || '/placeholder-image.jpg')}
                    >
                      <img
                        src={nursery.imagesPresignedUrls?.[index] || img || '/placeholder-image.jpg'}
                        alt={`${nursery.name} ${index + 1}`}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.onerror = null
                          target.src = '/placeholder-image.jpg'
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className='col-lg-7'>
            <div className='nursery-details__content'>
              <h2 className='nursery-details__title mb-20'>{nursery.name}</h2>

              <div className='nursery-details__location mb-20'>
                <i className='icon-map-marker mr-2' />
                <span>{nursery.location} - {nursery.address}</span>
              </div>

              <div className='nursery-details__trade-type mb-20'>
                <h5 className='mb-10'>Typ handlu:</h5>
                <p>{getTradeTypeLabel(nursery.tradeType)}</p>
              </div>

              {nursery.specializations && nursery.specializations.length > 0 && (
                <div className='nursery-details__tags mb-30'>
                  <h5 className='mb-10'>Specjalizacje:</h5>
                  <div>
                    {nursery.specializations.map((spec, index) => (
                      <span key={index} className='badge badge-pill badge-secondary mr-2 mb-2'>
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className='nursery-details__desc mb-30'>
                <h5 className='mb-10'>Opis:</h5>
                <p>{nursery.description}</p>
              </div>

              <div className='nursery-details__contact mb-30'>
                <h5 className='mb-10'>Kontakt:</h5>
                <ul className='list-unstyled'>
                  <li className='mb-2'>
                    <i className='icon-phone mr-2' /> {nursery.contactPhone}
                  </li>
                  <li className='mb-2'>
                    <i className='icon-envelope mr-2' /> {nursery.contactEmail}
                  </li>
                  {nursery.website && (
                    <li>
                      <i className='icon-link mr-2' />
                      <a
                        href={nursery.website.startsWith('http') ? nursery.website : `https://${nursery.website}`}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {nursery.website}
                      </a>
                    </li>
                  )}
                </ul>
              </div>

              {nursery.openingHours && nursery.openingHours.length > 0 && (
                <div className='nursery-details__hours mb-30'>
                  <h5 className='mb-10'>Godziny otwarcia:</h5>
                  <ul className='list-unstyled'>
                    {nursery.openingHours.map((hours, index) => (
                      <li key={index} className='mb-1'>{hours}</li>
                    ))}
                  </ul>
                </div>
              )}

              {username && isSuperadmin && (
                <div className='nursery-details__actions mt-30'>
                  <ButtonLink
                    to={`/nurseries/edit/${nursery.id}`}
                    className='tw-inline-flex tw-mr-3 tw-bg-[#0d253a] tw-text-white hover:tw-text-white'
                  >
                    <i className='fas fa-edit mr-2' /> Edytuj szkółkę
                  </ButtonLink>

                  <button
                    className={`
                                      tw-inline-flex tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    <i className='fas fa-trash-alt mr-2' /> Usuń szkółkę
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sekcja z kategoriami i roślinami */}
        {(nursery.nurseryPlants != null) && nursery.nurseryPlants.length > 0 && (
          <div className='row mt-60'>
            <div className='col-12'>
              <h3 className='mb-30 tw-font-semibold'>Dostępne rośliny</h3>
              <div className='plant-categories'>
                {Object.entries(groupPlantsByCategory(nursery.nurseryPlants)).map(([category, plants]) => (
                  <div key={category} className='plant-category mb-4'>
                    <div
                      className='plant-category__header d-flex justify-content-between align-items-center p-3 bg-light rounded cursor-pointer'
                      onClick={() => toggleCategory(category)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='d-flex align-items-center'>
                        <h4 className='mb-0'>{category}</h4>
                        <span className='badge badge-primary ml-2'>{plants.length}</span>
                      </div>
                      <i className={`fas fa-chevron-${expandedCategories[category] ? 'up' : 'down'}`} />
                    </div>

                    {expandedCategories[category] && (
                      <div className='plant-category__content mt-3'>
                        <div className='list-group'>
                          {plants.map((item) => (
                            <div key={item.plant.id} className='list-group-item tw-flex tw-items-center tw-justify-between tw-gap-2'>
                              <div>
                                <h5 className='mb-1'>{item.plant.name}</h5>
                                <small className='text-muted font-italic'>{item.plant.latinName}</small>
                              </div>

                              <ButtonLink
                                to={`/plants/${item.plant.id}`}
                                className='tw-inline-flex tw-mr-3 tw-bg-[#0d253a] tw-text-white hover:tw-text-white'
                              >
                                Zobacz
                              </ButtonLink>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {deleteConfirmOpen && (
        <div className='modal fade show' style={{ display: 'block' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Potwierdź usunięcie</h5>
                <button
                  type='button'
                  className='close'
                  onClick={() => setDeleteConfirmOpen(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                <p>Czy na pewno chcesz usunąć szkółkę "{nursery.name}"?</p>
                <p className='text-danger'>Ta operacja jest nieodwracalna.</p>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setDeleteConfirmOpen(false)}
                >
                  Anuluj
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={handleDeleteClick}
                >
                  Usuń szkółkę
                </button>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show' />
        </div>
      )}

      {openedImage && (
        <div
          className='image-modal'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={closeImageModal}
        >
          <div
            className='image-modal__content'
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%'
            }}
          >
            <img
              src={openedImage}
              alt='Powiększone zdjęcie'
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 100px)',
                display: 'block'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className='image-modal__close'
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-40px',
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer'
              }}
              onClick={closeImageModal}
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default NurseryDetails
